<template>
  <OnBoarding>
    <template>
      <CRow>
        <CCol>
          <OnBoardingHeader :title="$gettext('Create account')"/>

          <PAlert v-if="isErrorVisible" class="mb-2" color="danger">{{ errorMessage }}</PAlert>

          <CInput
              ref="email"
              :placeholder="$pgettext('auth.placeholder','E-mail')"
              v-model="v$.email.$model"
              :is-valid="!v$.email.$error"
              :label="$gettext('E-mail')"
              autocomplete="off"
              @keyup.enter="signup">
            <template v-slot:invalid-feedback>
              <div class="invalid-feedback d-block" v-for="error of v$.email.$errors" :key="error.$uid">
                <div class="error-msg">{{ error.$message }}</div>
              </div>
            </template>
          </CInput>


          <div class="d-flex mb-3">
            <CInputCheckbox custom
                            :checked.sync="v$.termsAgreement.$model"
                            :is-valid="!v$.termsAgreement.$error"
                            id="termsAgreement"
                            :placeholder="$pgettext('auth.placeholder','Terms and conditions')">
            </CInputCheckbox>
            <div>
              <label for="termsAgreement" class="form-label mb-0" v-html="termsAgreementText"/>
              <div class="invalid-feedback d-block" v-for="error of v$.termsAgreement.$errors" :key="error.$uid">
                <div class="error-msg" v-translate translate-content="auth.terms.error">You must accept the terms and
                  conditions
                </div>
              </div>
            </div>
          </div>


          <PButton :loading="loading" type="button" color="primary" block v-translate @click="signup">Create account</PButton>

          <div class="text-center info">
            <span v-translate>otherwise</span>
          </div>
          <SocialLogin allow-register/>
          <AlreadyHaveAccount/>
        </CCol>
      </CRow>
    </template>
  </OnBoarding>
</template>

<script>
import SocialLogin from "@/domain/login/components/providers/SocialLoginButtons.vue";
import {mapActions, mapGetters} from "vuex";
import {useVuelidate} from "@vuelidate/core";
import {methods, computed} from "@/domain/login/mixin";
import OnBoarding from "@/domain/login/OnBoarding.vue";
import AlreadyHaveAccount from "@/domain/login/components/AlreadyHaveAccount.vue";
import OnBoardingHeader from "@/domain/login/components/OnBoardingHeader.vue";
import {
  required$,
  email$,
  sameAs$
} from "@/domain/core/utils/validators";
import PButton from "@/components/PButton.vue";
import PAlert from "@/components/PAlert.vue";

export default {
  name: 'Register',
  components: {PAlert, PButton, OnBoardingHeader, AlreadyHaveAccount, OnBoarding, SocialLogin},

  data() {
    return {
      loading: false,
      email: null,
      termsAgreement: false,
      submitted: false,
      error: null
    }
  },
  mounted() {
    if (this.invitation)
      this.email = this.invitation.email

    this.$refs.email.$el.children[0].focus()
    this.checkQueryParams()
  },
  setup() {
    return {v$: useVuelidate()}
  },
  validations() {
    return {
      email: {required$, email$},
      termsAgreement: {sameAs: sameAs$(true)}
    }
  },
  computed: {
    ...mapGetters('login', ['userLanguage', 'registrationFlow']),
    ...computed,
    isValid() {
      return !this.v$.$invalid
    },
    isErrorVisible() {
      return !!this.error
    },
    errorMessage() {
      if (this.error) return this.error
      return null
    },
    termsAgreementText() {
      const translatedText = this.$gettext("I declare that I am at least 18 years old and have read and agree to the [TERMS_LINK] and the [PRIVACY_LINK] of GetPica.");

      const termsLink = `<a href="${this.policyUrl.terms}" target="_blank">${this.$gettext('Terms and Conditions')}</a>`;
      const privacyLink = `<a href="${this.policyUrl.privacy}" target="_blank">${this.$gettext('Privacy Policy')}</a>`;

      return translatedText
        .replace('[TERMS_LINK]', termsLink)
        .replace('[PRIVACY_LINK]', privacyLink);
    },
    policyUrl() {
      return {
        terms: `https://policy.getpica.com/${this.userLanguage}/termini-condizioni-vendita/`,
        privacy: `https://policy.getpica.com/${this.userLanguage}/privacy-policy-pica/`,
      }
    }
  },
  methods: {
    ...methods,
    ...mapActions('login', [
      'createUser',
      'getCurrentUser',
    ]),

    async signup() {
      if (!await this.v$.$validate()) return
      this.submitted = true
      this.error = null
      if (this.isValid) {
        try {
          this.loading = true
          await this.createUser({
            email: this.email
          })
          
          // The action handles both cases and sets registrationFlow
          // Always redirect to login page which will handle the appropriate flow
          await this.$router.push({
            name: 'login',
            query: { 
              email: this.email,
              flow: this.registrationFlow // Pass the flow type to login page
            }
          })
        } catch (e) {
          // Only show error if it wasn't handled by the action
          if (await this.manageLoginError(e, this.email)) return
          this.error = this.$gettext('An error occurred during registration. Please try again.')
        } finally {
          this.loading = false
        }
      }
    },
  }
}
</script>
